/**
 * Transaction process graph for product orders:
 *   - default-purchase
 */

/**
 * Transitions
 *
 * These strings must sync with values defined in Marketplace API,
 * since transaction objects given by API contain info about last transitions.
 * All the actions in API side happen in transitions,
 * so we need to understand what those strings mean.
 */

export const transitions = {
  // When a customer makes an order for a listing, a transaction is
  // created with the initial request-payment transition.
  // At this transition a PaymentIntent is created by Marketplace API.
  // After this transition, the actual payment must be made on client-side directly to Stripe.
  REQUEST_PAYMENT: 'transition/request-payment',

  // A customer can also initiate a transaction with an inquiry, and
  // then transition that with a request.
  INQUIRE: 'transition/inquire',
  REQUEST_PAYMENT_AFTER_INQUIRY: 'transition/request-payment-after-inquiry',

  // Stripe SDK might need to ask 3D security from customer, in a separate front-end step.
  // Therefore we need to make another transition to Marketplace API,
  // to tell that the payment is confirmed.
  CONFIRM_PAYMENT: 'transition/confirm-payment',

  // If the payment is not confirmed in the time limit set in transaction process (by default 15min)
  // the transaction will expire automatically.
  EXPIRE_PAYMENT: 'transition/expire-payment',

  // Provider or opeartor can mark the product shipped/delivered
  MARK_DELIVERED: 'transition/mark-delivered',
  OPERATOR_MARK_DELIVERED: 'transition/operator-mark-delivered',

  // Customer can mark the product received (e.g. picked up from provider)
  MARK_RECEIVED_FROM_PURCHASED: 'transition/mark-received-from-purchased',

  // Automatic cancellation happens if none marks the delivery happened
  AUTO_CANCEL: 'transition/auto-cancel',

  // Operator can cancel the purchase before product has been marked as delivered / received
  CANCEL: 'transition/cancel',

  // If provider has marked the product delivered (e.g. shipped),
  // customer can then mark the product received
  MARK_RECEIVED: 'transition/mark-received',

  // If customer doesn't mark the product received manually, it can happen automatically
  AUTO_MARK_RECEIVED: 'transition/auto-mark-received',

  // When provider has marked the product delivered, customer or operator can dispute the transaction
  DISPUTE: 'transition/dispute',
  OPERATOR_DISPUTE: 'transition/operator-dispute',

  // If nothing is done to disputed transaction it ends up to Canceled state
  AUTO_CANCEL_FROM_DISPUTED: 'transition/auto-cancel-from-disputed',

  // Operator can cancel disputed transaction manually
  CANCEL_FROM_DISPUTED: 'transition/cancel-from-disputed',

  // Operator can mark the disputed transaction as received
  MARK_RECEIVED_FROM_DISPUTED: 'transition/mark-received-from-disputed',

  RELEASE_PAYOUT_FROM_RECEIVED: 'transition/release-payout-from-received',
  MARK_PAYOUT_ELIGIBLE_FROM_RECEIVED: 'transition/mark-payout-eligible-from-received',
  MARK_PAYOUT_FAILED_FROM_RECEIVED: 'transition/mark-payout-failed-from-received',
  MARK_PAYOUT_BLOCKED_FROM_RECEIVED: 'transition/mark-payout-blocked-from-received',
  RESOLVE_PAYOUT_BLOCKED_TO_PENDING_FROM_RECEIVED:
    'transition/resolve-payout-blocked-to-pending-from-received',
  RESOLVE_PAYOUT_BLOCKED_TO_ELIGIBLE_FROM_RECEIVED:
    'transition/resolve-payout-blocked-to-eligible-from-received',
  RESOLVE_PAYOUT_BLOCKED_TO_CANCELED_FROM_RECEIVED:
    'transition/resolve-payout-blocked-to-canceled-from-received',

  // Reviews are given through transaction transitions. Review 1 can be
  // by provider or customer, and review 2 will be the other party of
  // the transaction.
  REVIEW_1_BY_PROVIDER: 'transition/review-1-by-provider',
  REVIEW_2_BY_PROVIDER: 'transition/review-2-by-provider',
  REVIEW_1_BY_CUSTOMER: 'transition/review-1-by-customer',
  REVIEW_2_BY_CUSTOMER: 'transition/review-2-by-customer',
  REVIEW_1_BY_PROVIDER_AFTER_PAYOUT_RELEASE: 'transition/review-1-by-provider-after-payout-release',
  REVIEW_2_BY_PROVIDER_AFTER_PAYOUT_RELEASE: 'transition/review-2-by-provider-after-payout-release',
  REVIEW_1_BY_CUSTOMER_AFTER_PAYOUT_RELEASE: 'transition/review-1-by-customer-after-payout-release',
  REVIEW_2_BY_CUSTOMER_AFTER_PAYOUT_RELEASE: 'transition/review-2-by-customer-after-payout-release',
  EXPIRE_CUSTOMER_REVIEW_PERIOD: 'transition/expire-customer-review-period',
  EXPIRE_PROVIDER_REVIEW_PERIOD: 'transition/expire-provider-review-period',
  EXPIRE_REVIEW_PERIOD: 'transition/expire-review-period',
  EXPIRE_CUSTOMER_REVIEW_PERIOD_AFTER_PAYOUT_RELEASE:
    'transition/expire-customer-review-period-after-payout-release',
  EXPIRE_PROVIDER_REVIEW_PERIOD_AFTER_PAYOUT_RELEASE:
    'transition/expire-provider-review-period-after-payout-release',
  EXPIRE_REVIEW_PERIOD_AFTER_PAYOUT_RELEASE: 'transition/expire-review-period-after-payout-release',
  RELEASE_PAYOUT_FROM_REVIEWED_BY_PROVIDER: 'transition/release-payout-from-reviewed-by-provider',
  MARK_PAYOUT_ELIGIBLE_FROM_REVIEWED_BY_PROVIDER:
    'transition/mark-payout-eligible-from-reviewed-by-provider',
  MARK_PAYOUT_FAILED_FROM_REVIEWED_BY_PROVIDER:
    'transition/mark-payout-failed-from-reviewed-by-provider',
  MARK_PAYOUT_BLOCKED_FROM_REVIEWED_BY_PROVIDER:
    'transition/mark-payout-blocked-from-reviewed-by-provider',
  RESOLVE_PAYOUT_BLOCKED_TO_PENDING_FROM_REVIEWED_BY_PROVIDER:
    'transition/resolve-payout-blocked-to-pending-from-reviewed-by-provider',
  RESOLVE_PAYOUT_BLOCKED_TO_ELIGIBLE_FROM_REVIEWED_BY_PROVIDER:
    'transition/resolve-payout-blocked-to-eligible-from-reviewed-by-provider',
  RESOLVE_PAYOUT_BLOCKED_TO_CANCELED_FROM_REVIEWED_BY_PROVIDER:
    'transition/resolve-payout-blocked-to-canceled-from-reviewed-by-provider',
  RELEASE_PAYOUT_FROM_REVIEWED_BY_CUSTOMER: 'transition/release-payout-from-reviewed-by-customer',
  MARK_PAYOUT_ELIGIBLE_FROM_REVIEWED_BY_CUSTOMER:
    'transition/mark-payout-eligible-from-reviewed-by-customer',
  MARK_PAYOUT_FAILED_FROM_REVIEWED_BY_CUSTOMER:
    'transition/mark-payout-failed-from-reviewed-by-customer',
  MARK_PAYOUT_BLOCKED_FROM_REVIEWED_BY_CUSTOMER:
    'transition/mark-payout-blocked-from-reviewed-by-customer',
  RESOLVE_PAYOUT_BLOCKED_TO_PENDING_FROM_REVIEWED_BY_CUSTOMER:
    'transition/resolve-payout-blocked-to-pending-from-reviewed-by-customer',
  RESOLVE_PAYOUT_BLOCKED_TO_ELIGIBLE_FROM_REVIEWED_BY_CUSTOMER:
    'transition/resolve-payout-blocked-to-eligible-from-reviewed-by-customer',
  RESOLVE_PAYOUT_BLOCKED_TO_CANCELED_FROM_REVIEWED_BY_CUSTOMER:
    'transition/resolve-payout-blocked-to-canceled-from-reviewed-by-customer',
  RELEASE_PAYOUT_FROM_REVIEWED: 'transition/release-payout-from-reviewed',
  MARK_PAYOUT_ELIGIBLE_FROM_REVIEWED: 'transition/mark-payout-eligible-from-reviewed',
  MARK_PAYOUT_FAILED_FROM_REVIEWED: 'transition/mark-payout-failed-from-reviewed',
  MARK_PAYOUT_BLOCKED_FROM_REVIEWED: 'transition/mark-payout-blocked-from-reviewed',
  RESOLVE_PAYOUT_BLOCKED_TO_PENDING_FROM_REVIEWED:
    'transition/resolve-payout-blocked-to-pending-from-reviewed',
  RESOLVE_PAYOUT_BLOCKED_TO_ELIGIBLE_FROM_REVIEWED:
    'transition/resolve-payout-blocked-to-eligible-from-reviewed',
  RESOLVE_PAYOUT_BLOCKED_TO_CANCELED_FROM_REVIEWED:
    'transition/resolve-payout-blocked-to-canceled-from-reviewed',
};

/**
 * States
 *
 * These constants are only for making it clear how transitions work together.
 * You should not use these constants outside of this file.
 *
 * Note: these states are not in sync with states used transaction process definitions
 *       in Marketplace API. Only last transitions are passed along transaction object.
 */

export const states = {
  INITIAL: 'initial',
  INQUIRY: 'inquiry',
  PENDING_PAYMENT: 'pending-payment',
  PAYMENT_EXPIRED: 'payment-expired',
  PURCHASED: 'purchased',
  DELIVERED: 'delivered',
  RECEIVED: 'received',
  DISPUTED: 'disputed',
  CANCELED: 'canceled',
  COMPLETED_PAYOUT_RELEASED: 'completed-payout-released',
  REVIEWED: 'reviewed',
  REVIEWED_PAYOUT_RELEASED: 'reviewed-payout-released',
  REVIEWED_BY_CUSTOMER: 'reviewed-by-customer',
  REVIEWED_BY_CUSTOMER_PAYOUT_RELEASED: 'reviewed-by-customer-payout-released',
  REVIEWED_BY_PROVIDER: 'reviewed-by-provider',
  REVIEWED_BY_PROVIDER_PAYOUT_RELEASED: 'reviewed-by-provider-payout-released',
};

/**
 * Description of transaction process graph
 *
 * You should keep this in sync with transaction process defined in Marketplace API
 *
 * Note: we don't use yet any state machine library,
 *       but this description format is following Xstate (FSM library)
 *       https://xstate.js.org/docs/
 */
export const graph = {
  // id is defined only to support Xstate format.
  // However if you have multiple transaction processes defined,
  // it is best to keep them in sync with transaction process aliases.
  id: 'default-purchase/release-1',

  // This 'initial' state is a starting point for new transaction
  initial: states.INITIAL,

  // States
  states: {
    [states.INITIAL]: {
      on: {
        [transitions.INQUIRE]: states.INQUIRY,
        [transitions.REQUEST_PAYMENT]: states.PENDING_PAYMENT,
      },
    },
    [states.INQUIRY]: {
      on: {
        [transitions.REQUEST_PAYMENT_AFTER_INQUIRY]: states.PENDING_PAYMENT,
      },
    },

    [states.PENDING_PAYMENT]: {
      on: {
        [transitions.EXPIRE_PAYMENT]: states.PAYMENT_EXPIRED,
        [transitions.CONFIRM_PAYMENT]: states.PURCHASED,
      },
    },

    [states.PAYMENT_EXPIRED]: {},
    [states.PURCHASED]: {
      on: {
        [transitions.MARK_DELIVERED]: states.DELIVERED,
        [transitions.OPERATOR_MARK_DELIVERED]: states.DELIVERED,
        [transitions.MARK_RECEIVED_FROM_PURCHASED]: states.RECEIVED,
        [transitions.AUTO_CANCEL]: states.CANCELED,
        [transitions.CANCEL]: states.CANCELED,
      },
    },

    [states.CANCELED]: {},

    [states.DELIVERED]: {
      on: {
        [transitions.MARK_RECEIVED]: states.RECEIVED,
        [transitions.AUTO_MARK_RECEIVED]: states.RECEIVED,
        [transitions.DISPUTE]: states.DISPUTED,
        [transitions.OPERATOR_DISPUTE]: states.DISPUTED,
      },
    },

    [states.DISPUTED]: {
      on: {
        [transitions.AUTO_CANCEL_FROM_DISPUTED]: states.CANCELED,
        [transitions.CANCEL_FROM_DISPUTED]: states.CANCELED,
        [transitions.MARK_RECEIVED_FROM_DISPUTED]: states.RECEIVED,
      },
    },

    [states.RECEIVED]: {
      on: {
        [transitions.EXPIRE_REVIEW_PERIOD]: states.REVIEWED,
        [transitions.REVIEW_1_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
        [transitions.REVIEW_1_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
        [transitions.RELEASE_PAYOUT_FROM_RECEIVED]: states.COMPLETED_PAYOUT_RELEASED,
        [transitions.MARK_PAYOUT_ELIGIBLE_FROM_RECEIVED]: states.RECEIVED,
        [transitions.MARK_PAYOUT_FAILED_FROM_RECEIVED]: states.RECEIVED,
        [transitions.MARK_PAYOUT_BLOCKED_FROM_RECEIVED]: states.RECEIVED,
        [transitions.RESOLVE_PAYOUT_BLOCKED_TO_PENDING_FROM_RECEIVED]: states.RECEIVED,
        [transitions.RESOLVE_PAYOUT_BLOCKED_TO_ELIGIBLE_FROM_RECEIVED]:
          states.COMPLETED_PAYOUT_RELEASED,
        [transitions.RESOLVE_PAYOUT_BLOCKED_TO_CANCELED_FROM_RECEIVED]: states.CANCELED,
      },
    },

    [states.COMPLETED_PAYOUT_RELEASED]: {
      on: {
        [transitions.EXPIRE_REVIEW_PERIOD_AFTER_PAYOUT_RELEASE]: states.REVIEWED_PAYOUT_RELEASED,
        [transitions.REVIEW_1_BY_CUSTOMER_AFTER_PAYOUT_RELEASE]:
          states.REVIEWED_BY_CUSTOMER_PAYOUT_RELEASED,
        [transitions.REVIEW_1_BY_PROVIDER_AFTER_PAYOUT_RELEASE]:
          states.REVIEWED_BY_PROVIDER_PAYOUT_RELEASED,
      },
    },

    [states.REVIEWED_BY_CUSTOMER]: {
      on: {
        [transitions.REVIEW_2_BY_PROVIDER]: states.REVIEWED,
        [transitions.EXPIRE_PROVIDER_REVIEW_PERIOD]: states.REVIEWED,
        [transitions.RELEASE_PAYOUT_FROM_REVIEWED_BY_CUSTOMER]: states.REVIEWED_PAYOUT_RELEASED,
        [transitions.MARK_PAYOUT_ELIGIBLE_FROM_REVIEWED_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
        [transitions.MARK_PAYOUT_FAILED_FROM_REVIEWED_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
        [transitions.MARK_PAYOUT_BLOCKED_FROM_REVIEWED_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
        [transitions.RESOLVE_PAYOUT_BLOCKED_TO_PENDING_FROM_REVIEWED_BY_CUSTOMER]:
          states.REVIEWED_BY_CUSTOMER,
        [transitions.RESOLVE_PAYOUT_BLOCKED_TO_ELIGIBLE_FROM_REVIEWED_BY_CUSTOMER]:
          states.REVIEWED_PAYOUT_RELEASED,
        [transitions.RESOLVE_PAYOUT_BLOCKED_TO_CANCELED_FROM_REVIEWED_BY_CUSTOMER]: states.CANCELED,
      },
    },
    [states.REVIEWED_BY_CUSTOMER_PAYOUT_RELEASED]: {
      on: {
        [transitions.REVIEW_2_BY_PROVIDER_AFTER_PAYOUT_RELEASE]: states.REVIEWED_PAYOUT_RELEASED,
        [transitions.EXPIRE_PROVIDER_REVIEW_PERIOD_AFTER_PAYOUT_RELEASE]:
          states.REVIEWED_PAYOUT_RELEASED,
      },
    },
    [states.REVIEWED_BY_PROVIDER]: {
      on: {
        [transitions.REVIEW_2_BY_CUSTOMER]: states.REVIEWED,
        [transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD]: states.REVIEWED,
        [transitions.RELEASE_PAYOUT_FROM_REVIEWED_BY_PROVIDER]: states.REVIEWED_PAYOUT_RELEASED,
        [transitions.MARK_PAYOUT_ELIGIBLE_FROM_REVIEWED_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
        [transitions.MARK_PAYOUT_FAILED_FROM_REVIEWED_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
        [transitions.MARK_PAYOUT_BLOCKED_FROM_REVIEWED_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
        [transitions.RESOLVE_PAYOUT_BLOCKED_TO_PENDING_FROM_REVIEWED_BY_PROVIDER]:
          states.REVIEWED_BY_PROVIDER,
        [transitions.RESOLVE_PAYOUT_BLOCKED_TO_ELIGIBLE_FROM_REVIEWED_BY_PROVIDER]:
          states.REVIEWED_PAYOUT_RELEASED,
        [transitions.RESOLVE_PAYOUT_BLOCKED_TO_CANCELED_FROM_REVIEWED_BY_PROVIDER]: states.CANCELED,
      },
    },
    [states.REVIEWED_BY_PROVIDER_PAYOUT_RELEASED]: {
      on: {
        [transitions.REVIEW_2_BY_CUSTOMER_AFTER_PAYOUT_RELEASE]: states.REVIEWED_PAYOUT_RELEASED,
        [transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD_AFTER_PAYOUT_RELEASE]:
          states.REVIEWED_PAYOUT_RELEASED,
      },
    },
    [states.REVIEWED]: {
      on: {
        [transitions.RELEASE_PAYOUT_FROM_REVIEWED]: states.REVIEWED_PAYOUT_RELEASED,
        [transitions.MARK_PAYOUT_ELIGIBLE_FROM_REVIEWED]: states.REVIEWED,
        [transitions.MARK_PAYOUT_FAILED_FROM_REVIEWED]: states.REVIEWED,
        [transitions.MARK_PAYOUT_BLOCKED_FROM_REVIEWED]: states.REVIEWED,
        [transitions.RESOLVE_PAYOUT_BLOCKED_TO_PENDING_FROM_REVIEWED]: states.REVIEWED,
        [transitions.RESOLVE_PAYOUT_BLOCKED_TO_ELIGIBLE_FROM_REVIEWED]:
          states.REVIEWED_PAYOUT_RELEASED,
        [transitions.RESOLVE_PAYOUT_BLOCKED_TO_CANCELED_FROM_REVIEWED]: states.CANCELED,
      },
      type: 'final',
    },
    [states.REVIEWED_PAYOUT_RELEASED]: {
      type: 'final',
    },
  },
};

// Check if a transition is the kind that should be rendered
// when showing transition history (e.g. ActivityFeed)
// The first transition and most of the expiration transitions made by system are not relevant
export const isRelevantPastTransition = transition => {
  return [
    transitions.CONFIRM_PAYMENT,
    transitions.AUTO_CANCEL,
    transitions.CANCEL,
    transitions.MARK_DELIVERED,
    transitions.OPERATOR_MARK_DELIVERED,
    transitions.RELEASE_PAYOUT_FROM_RECEIVED,
    transitions.MARK_PAYOUT_ELIGIBLE_FROM_RECEIVED,
    transitions.MARK_PAYOUT_FAILED_FROM_RECEIVED,
    transitions.MARK_PAYOUT_BLOCKED_FROM_RECEIVED,
    transitions.RESOLVE_PAYOUT_BLOCKED_TO_PENDING_FROM_RECEIVED,
    transitions.RESOLVE_PAYOUT_BLOCKED_TO_ELIGIBLE_FROM_RECEIVED,
    transitions.RESOLVE_PAYOUT_BLOCKED_TO_CANCELED_FROM_RECEIVED,
    transitions.DISPUTE,
    transitions.OPERATOR_DISPUTE,
    transitions.AUTO_CANCEL_FROM_DISPUTED,
    transitions.CANCEL_FROM_DISPUTED,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.REVIEW_1_BY_CUSTOMER_AFTER_PAYOUT_RELEASE,
    transitions.REVIEW_1_BY_PROVIDER_AFTER_PAYOUT_RELEASE,
    transitions.REVIEW_2_BY_CUSTOMER_AFTER_PAYOUT_RELEASE,
    transitions.REVIEW_2_BY_PROVIDER_AFTER_PAYOUT_RELEASE,
    transitions.RELEASE_PAYOUT_FROM_REVIEWED_BY_PROVIDER,
    transitions.MARK_PAYOUT_ELIGIBLE_FROM_REVIEWED_BY_PROVIDER,
    transitions.MARK_PAYOUT_FAILED_FROM_REVIEWED_BY_PROVIDER,
    transitions.MARK_PAYOUT_BLOCKED_FROM_REVIEWED_BY_PROVIDER,
    transitions.RESOLVE_PAYOUT_BLOCKED_TO_PENDING_FROM_REVIEWED_BY_PROVIDER,
    transitions.RESOLVE_PAYOUT_BLOCKED_TO_ELIGIBLE_FROM_REVIEWED_BY_PROVIDER,
    transitions.RESOLVE_PAYOUT_BLOCKED_TO_CANCELED_FROM_REVIEWED_BY_PROVIDER,
    transitions.RELEASE_PAYOUT_FROM_REVIEWED_BY_CUSTOMER,
    transitions.MARK_PAYOUT_ELIGIBLE_FROM_REVIEWED_BY_CUSTOMER,
    transitions.MARK_PAYOUT_FAILED_FROM_REVIEWED_BY_CUSTOMER,
    transitions.MARK_PAYOUT_BLOCKED_FROM_REVIEWED_BY_CUSTOMER,
    transitions.RESOLVE_PAYOUT_BLOCKED_TO_PENDING_FROM_REVIEWED_BY_CUSTOMER,
    transitions.RESOLVE_PAYOUT_BLOCKED_TO_ELIGIBLE_FROM_REVIEWED_BY_CUSTOMER,
    transitions.RESOLVE_PAYOUT_BLOCKED_TO_CANCELED_FROM_REVIEWED_BY_CUSTOMER,
    transitions.RELEASE_PAYOUT_FROM_REVIEWED,
    transitions.MARK_PAYOUT_ELIGIBLE_FROM_REVIEWED,
    transitions.MARK_PAYOUT_FAILED_FROM_REVIEWED,
    transitions.MARK_PAYOUT_BLOCKED_FROM_REVIEWED,
    transitions.RESOLVE_PAYOUT_BLOCKED_TO_PENDING_FROM_REVIEWED,
    transitions.RESOLVE_PAYOUT_BLOCKED_TO_ELIGIBLE_FROM_REVIEWED,
    transitions.RESOLVE_PAYOUT_BLOCKED_TO_CANCELED_FROM_REVIEWED,
    transitions.EXPIRE_REVIEW_PERIOD_AFTER_PAYOUT_RELEASE,
    transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD_AFTER_PAYOUT_RELEASE,
    transitions.EXPIRE_PROVIDER_REVIEW_PERIOD_AFTER_PAYOUT_RELEASE,
  ].includes(transition);
};
export const isCustomerReview = transition => {
  return [
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_1_BY_CUSTOMER_AFTER_PAYOUT_RELEASE,
    transitions.REVIEW_2_BY_CUSTOMER_AFTER_PAYOUT_RELEASE,
  ].includes(transition);
};

export const isProviderReview = transition => {
  return [
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.REVIEW_1_BY_PROVIDER_AFTER_PAYOUT_RELEASE,
    transitions.REVIEW_2_BY_PROVIDER_AFTER_PAYOUT_RELEASE,
  ].includes(transition);
};

// Check if the given transition is privileged.
//
// Privileged transitions need to be handled from a secure context,
// i.e. the backend. This helper is used to check if the transition
// should go through the local API endpoints, or if using JS SDK is
// enough.
export const isPrivileged = transition => {
  return [
    transitions.REQUEST_PAYMENT,
    transitions.REQUEST_PAYMENT_AFTER_INQUIRY,
    transitions.MARK_RECEIVED_FROM_PURCHASED,
    transitions.MARK_RECEIVED,
    transitions.MARK_RECEIVED_FROM_DISPUTED,
  ].includes(transition);
};

// Check when transaction is completed (item is received and review notifications sent)
export const isCompleted = transition => {
  const txCompletedTransitions = [
    transitions.MARK_RECEIVED_FROM_PURCHASED,
    transitions.MARK_RECEIVED,
    transitions.MARK_RECEIVED_FROM_DISPUTED,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.EXPIRE_REVIEW_PERIOD,
    transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD,
    transitions.EXPIRE_PROVIDER_REVIEW_PERIOD,
    transitions.REVIEW_1_BY_CUSTOMER_AFTER_PAYOUT_RELEASE,
    transitions.REVIEW_1_BY_PROVIDER_AFTER_PAYOUT_RELEASE,
    transitions.REVIEW_2_BY_CUSTOMER_AFTER_PAYOUT_RELEASE,
    transitions.REVIEW_2_BY_PROVIDER_AFTER_PAYOUT_RELEASE,
    transitions.EXPIRE_REVIEW_PERIOD_AFTER_PAYOUT_RELEASE,
    transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD_AFTER_PAYOUT_RELEASE,
    transitions.EXPIRE_PROVIDER_REVIEW_PERIOD_AFTER_PAYOUT_RELEASE,
  ];
  return txCompletedTransitions.includes(transition);
};

// Check when transaction is refunded (order did not happen)
// In these transitions action/stripe-refund-payment is called
export const isRefunded = transition => {
  const txRefundedTransitions = [
    transitions.EXPIRE_PAYMENT,
    transitions.CANCEL,
    transitions.AUTO_CANCEL,
    transitions.AUTO_CANCEL_FROM_DISPUTED,
    transitions.CANCEL_FROM_DISPUTED,
  ];
  return txRefundedTransitions.includes(transition);
};

export const statesNeedingProviderAttention = [states.PURCHASED];
